/**
 * Keeps focus on the button that started a Turbo form submission once a refused save re-renders
 * the page. Turbo disables that button for the request, so focus can be on the body before the
 * render begins: the button is recorded at `turbo:submit-start`, not found by its focus at render.
 * Always uses `preventScroll`.
 */

import {
   applyDeliberateAutofocus,
   captureFocusSnapshot,
   formRef,
   isFocusable,
   resolveForm,
   userAlreadyMovedToDifferentControl,
   type FocusSnapshot,
   type FormRef,
} from "./focus-restore";

export type PressedSubmitter = {
   /** The Turbo form submission that pressed the button. */
   submission: object;
   button: HTMLElement;
   form: FormRef | null;
   /** Index of the button among its form's submit controls. */
   position: number;
   /** What had focus when the submission started (the field, after Enter in a field). */
   focusedAtStart: FocusSnapshot | null;
};

function submitControls(form: HTMLFormElement): HTMLElement[] {
   return Array.from(form.elements).filter(
      (el): el is HTMLElement =>
         (el instanceof HTMLButtonElement && el.type === "submit") ||
         (el instanceof HTMLInputElement && (el.type === "submit" || el.type === "image")),
   );
}

export function capturePressedSubmitter(
   submission: object,
   submitter: unknown,
   formElement: unknown,
): PressedSubmitter | null {
   if (!(submitter instanceof HTMLElement)) {
      return null;
   }

   const form = formElement instanceof HTMLFormElement ? formElement : null;
   return {
      submission,
      button: submitter,
      form: formRef(form),
      position: form ? submitControls(form).indexOf(submitter) : -1,
      focusedAtStart: captureFocusSnapshot(),
   };
}

function findPressedButton(pressed: PressedSubmitter): HTMLElement | null {
   if (pressed.button.isConnected) {
      return pressed.button;
   }

   const form = resolveForm(pressed.form);
   return form ? (submitControls(form)[pressed.position] ?? null) : null;
}

/**
 * Focuses the page's deliberate autofocus target when it has one, else the pressed button, on
 * the refused page. Leaves focus alone when the person moved to another interactive control
 * after pressing, or when there is no autofocus target and the button is gone, hidden or still
 * disabled.
 */
export function restorePressedSubmitter(pressed: PressedSubmitter | null): void {
   if (!pressed || userAlreadyMovedToDifferentControl(document.activeElement, pressed.focusedAtStart?.identity ?? null)) {
      return;
   }

   // Turbo does not focus an autofocus field on a re-render, and the button's own focus is gone.
   if (applyDeliberateAutofocus()) {
      return;
   }

   const button = findPressedButton(pressed);
   if (button && isFocusable(button)) {
      button.focus({ preventScroll: true });
   }
}
